import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, orgID, useList, useSession } from '@yingbaba/api';
import {
  Button,
  Empty,
  FileAttachment,
  Modal,
  Pagination,
  QueryState,
  Select,
  compactFileSize,
  useToast,
  useUnsavedChanges,
} from '@yingbaba/ui';
import { FolderOpen } from '@yingbaba/icons';
import { datetime } from '@yingbaba/utils';
import type { FileLibraryItem } from '@yingbaba/types';

type Selection = {
  file: FileLibraryItem;
  key: string;
  status: 'selected' | 'adding' | 'done' | 'failed';
  error?: string;
};

type LibraryProps = { projectID: string; onAdded: () => void };

export function ProjectFileLibrary({ projectID, onAdded }: LibraryProps) {
  const session = useSession();
  return (
    <LibraryDialog
      key={`${projectID}:${orgID()}:${session.data?.account.id}`}
      projectID={projectID}
      onAdded={onAdded}
    />
  );
}

function LibraryDialog({ projectID, onAdded }: LibraryProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [versions, setVersions] = useState('latest');
  const [selection, setSelection] = useState<Selection[]>([]);
  const latest = useRef<Selection[]>([]);
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const alive = useRef(true);
  const qc = useQueryClient();
  const toast = useToast();
  const library = useList<FileLibraryItem>(
    `/workspace/file-library?page=${page}&q=${encodeURIComponent(search)}&type=${type}&versions=${versions}`,
    open,
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  useUnsavedChanges({
    dirty: busy,
    title: '素材仍在添加中',
    description: '已添加的素材会保留。离开后，未完成的素材需要重新选择。',
    continueLabel: '继续等待',
    discardLabel: '停止并离开',
    discard: () => controller.current?.abort(),
  });
  function update(value: Selection[]) {
    latest.current = value;
    setSelection(value);
  }
  function select(file: FileLibraryItem, checked: boolean) {
    if (running.current) return;
    if (checked && latest.current.length >= 20) {
      setError('每次最多选择 20 个素材，请先添加或减少选择。');
      return;
    }
    setError('');
    setShare(false);
    update(
      checked
        ? [...latest.current, { file, key: crypto.randomUUID(), status: 'selected' }]
        : latest.current.filter((item) => item.file.id !== file.id),
    );
  }
  async function add() {
    const batch = latest.current.filter((item) => item.status !== 'done');
    if (running.current || !share || !batch.length) return;
    running.current = true;
    setBusy(true);
    setError('');
    const organization = orgID();
    const attempt = new AbortController();
    controller.current = attempt;
    const current = () => alive.current && !attempt.signal.aborted && orgID() === organization;
    try {
      // Sequential requests keep permissions/idempotency on the existing endpoint
      // and avoid a burst of uploads or writes. Each version keeps its retry key.
      for (const item of batch) {
        if (!current()) break;
        const patch = (value: Partial<Selection>) => {
          if (current())
            update(
              latest.current.map((entry) =>
                entry.file.id === item.file.id ? { ...entry, ...value } : entry,
              ),
            );
        };
        patch({ status: 'adding', error: undefined });
        try {
          await api(`/projects/${projectID}/files/reuse`, {
            method: 'POST',
            headers: { 'Idempotency-Key': item.key },
            body: JSON.stringify({ file_id: item.file.id, share_confirmed: true }),
            signal: attempt.signal,
          });
          patch({ status: 'done' });
        } catch (failure) {
          patch({
            status: 'failed',
            error: failure instanceof Error ? failure.message : '添加未完成，请重试',
          });
        }
      }
      if (current()) {
        if (latest.current.some((item) => item.status === 'done')) onAdded();
        void qc.invalidateQueries({
          predicate: (q) =>
            q.queryKey[1] === organization &&
            (/files|file-library/.test(String(q.queryKey[0])) ||
              q.queryKey[0] === `/projects/${projectID}`),
        });
        if (latest.current.every((item) => item.status === 'done')) {
          toast(`已添加 ${latest.current.length} 个素材`);
          setOpen(false);
        }
      }
    } finally {
      running.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const done = selection.filter((item) => item.status === 'done').length;
  const failed = selection.some((item) => item.status === 'failed');
  const completed = selection.length > 0 && done === selection.length;
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setOpen(true);
          setPage(1);
          setQuery('');
          setSearch('');
          setType('');
          setVersions('latest');
          update([]);
          setShare(false);
          setError('');
        }}
      >
        <FolderOpen size={16} />
        从素材库引入
      </Button>
      <Modal
        title="从组织素材库引入"
        description="选择已有素材，无需再次上传。添加后固定使用所选版本，不随来源更新。"
        className="file-library-dialog"
        open={open}
        busy={busy}
        onOpenChange={(value) => {
          if (!running.current) setOpen(value);
        }}
        footer={
          <div className="file-library-footer">
            <label className="project-tool-consent">
              <input
                type="checkbox"
                aria-label="确认素材共享权利"
                checked={share}
                disabled={!selection.length || completed || busy}
                onChange={(event) => setShare(event.target.checked)}
              />
              我有权将所选素材共享给本项目成员
            </label>
            <div className="file-library-footer-actions">
              <span className="small muted file-library-count" role="status">
                {done || busy
                  ? `已添加 ${done} / ${selection.length} 个`
                  : `已选 ${selection.length} / 20 个`}
              </span>
              <Button
                disabled={!selection.length || (!completed && !share)}
                loading={busy}
                onClick={() => (completed ? setOpen(false) : void add())}
              >
                {completed ? '完成' : failed ? '重试未完成项' : '添加到项目'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          className="row project-tool-search file-library-search"
          role="search"
          aria-label="查找组织素材"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(query.trim());
            setPage(1);
          }}
        >
          <input
            className="input"
            aria-label="搜索素材文件"
            placeholder="搜索文件名或来源项目"
            maxLength={200}
            value={query}
            disabled={busy}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button type="submit" disabled={busy}>
            搜索
          </Button>
        </form>
        <div className="file-library-filters">
          <Select
            aria-label="素材库文件类型"
            value={type}
            disabled={busy}
            onChange={(value) => {
              setType(value);
              setPage(1);
            }}
            options={[
              { value: '', label: '全部类型' },
              { value: 'image', label: '图片' },
              { value: 'video', label: '视频' },
              { value: 'audio', label: '音频' },
              { value: 'document', label: '文档' },
              { value: 'other', label: '其他文件' },
            ]}
          />
          <Select
            aria-label="素材版本范围"
            value={versions}
            disabled={busy}
            onChange={(value) => {
              setVersions(value);
              setPage(1);
            }}
            options={[
              { value: 'latest', label: '仅最新版本' },
              { value: 'all', label: '包含历史版本' },
            ]}
          />
        </div>
        <QueryState query={library}>
          {!library.data?.items.length && (
            <Empty
              title={search || type ? '没有找到相关素材' : '还没有可复用的素材'}
              description={
                search || type
                  ? '试试其他关键词、文件类型或版本范围。'
                  : '本组织上传且你有权访问的项目文件，会在这里集中展示。'
              }
            />
          )}
          <div className="file-library-options">
            {library.data?.items.map((file) => {
              const item = selection.find((entry) => entry.file.id === file.id);
              return (
                <label className="list-item file-library-option" key={file.id}>
                  <input
                    type="checkbox"
                    aria-label={`选择 ${file.name} V${file.version || 1}`}
                    disabled={busy || item?.status === 'done'}
                    checked={!!item}
                    onChange={(event) => select(file, event.target.checked)}
                  />
                  <span className="file-library-info">
                    <strong>{file.name}</strong>
                    <span className="muted small">
                      V{file.version || 1} · {compactFileSize(file.size)} ·{' '}
                      {file.created_at ? datetime(file.created_at) : '上传时间未记录'}
                    </span>
                    <span className="muted small">
                      {file.project_title || '来源项目'}
                      {file.uploader_name ? ` · ${file.uploader_name} 上传` : ''}
                      {file.project_id === projectID ? ' · 本项目文件' : ''}
                    </span>
                    {file.version_note && <span className="small">{file.version_note}</span>}
                    {item?.status === 'done' && <span className="small">已添加</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </QueryState>
        <fieldset className="file-library-pagination" disabled={busy}>
          <Pagination
            page={page}
            size={library.data?.page_size || 30}
            total={library.data?.total || 0}
            onPage={setPage}
          />
        </fieldset>
        {!!selection.length && (
          <section className="file-library-selection" aria-label="已选素材">
            <h3>已选素材（{selection.length}）</h3>
            <p className="small muted">可在添加前预览。切换筛选或翻页会保留选择。</p>
            <div className="file-library-selected-list">
              {selection.map(({ file, status, error: failure }) => (
                <div className={`file-library-selected is-${status}`} key={file.id}>
                  <FileAttachment
                    name={file.name}
                    mime={file.mime}
                    size={file.size}
                    previewPath={`/files/${file.id}/preview`}
                    downloadPath={`/files/${file.id}/download`}
                  />
                  <div className="file-library-selected-meta">
                    <span className="small muted">
                      {file.project_title || '来源项目'} · 固定使用 V{file.version || 1}
                      {status === 'done' ? ' · 已添加' : status === 'adding' ? ' · 正在添加…' : ''}
                    </span>
                    {status !== 'done' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        aria-label={`移除 ${file.name} V${file.version || 1}`}
                        onClick={() => select(file, false)}
                      >
                        移除
                      </Button>
                    )}
                  </div>
                  {failure && (
                    <p className="small form-error" role="alert">
                      {failure}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {error && (
          <p className="small form-error" role="alert">
            {error}
          </p>
        )}
      </Modal>
    </>
  );
}
